import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { FlowNodeDef } from "./types";
import type { InternalStep } from "./internalSteps";
import { kindAccent } from "./kindColors";

const openIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulseBox = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

type Props = {
  node: FlowNodeDef;
  left: number;
  top: number;
  width: number;
  steps: InternalStep[];
  subStepIndex: number;
  summary: string;
};

export default function NodeExpandCallout({
  node,
  left,
  top,
  width,
  steps,
  subStepIndex,
  summary,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = kindAccent(theme, node.kind);
  const activeStep =
    subStepIndex >= 0 && subStepIndex < steps.length ? steps[subStepIndex] : null;

  if (!activeStep) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        left,
        top,
        width,
        zIndex: 6,
        animation: `${openIn} 0.25s ease`,
        pointerEvents: "none",
      }}
      aria-live="polite"
    >
      <Box
        sx={{
          width: 2,
          height: 10,
          mx: "auto",
          mb: 0.25,
          bgcolor: accent,
          borderRadius: 1,
          opacity: 0.7,
        }}
      />
      <Box
        key={activeStep.id}
        sx={{
          border: "1px solid",
          borderColor: accent,
          borderRadius: 1.5,
          bgcolor: (th) =>
            th.palette.mode === "dark"
              ? alpha(th.palette.background.paper, 0.96)
              : alpha(th.palette.background.paper, 0.98),
          boxShadow: `0 6px 18px ${alpha(accent, 0.14)}`,
          p: 1.25,
          animation: `${pulseBox} 1.2s ease-in-out infinite`,
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            display: "block",
            mb: 0.5,
            color: accent,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            fontSize: 10,
          }}
        >
          {t("docs.useCaseFlows.calloutTitle")}
          {" · "}
          {subStepIndex + 1}/{steps.length}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{ display: "block", lineHeight: 1.35, mb: 0.5 }}
        >
          {activeStep.label}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", lineHeight: 1.45 }}
        >
          {activeStep.detail || summary}
        </Typography>
      </Box>
    </Box>
  );
}
